import React from "react";
import { useMsal } from "@azure/msal-react";
import { useParams, Link } from "react-router-dom";
import { getToken } from "../api/auth";
import {
  CycleDetail,
  PatchCyclePayload,
  ReviewItem,
  ReassignS2Response,
  getCycleDrilldown,
  patchCycle,
  reassignS2,
} from "../api/manageCyclesApi";
import { formatDate, statusStyle, deadlineBadge } from "./AdminCyclesPage";
import { getMaxAllowedDeadline } from "../utils/soxCeiling";
import { useDebouncedUserLookup } from "../hooks/useDebouncedUserLookup";

// ── datetime-local conversion ───────────────────────────────────────────────
function isoToDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Decision badge ──────────────────────────────────────────────────────────
function decisionBadge(decision: string | null): React.ReactNode {
  if (!decision) return <span style={{ color: "#888" }}>—</span>;
  const palette: Record<string, { bg: string; fg: string }> = {
    "Approve":      { bg: "#dff6dd", fg: "#107c10" },
    "Deny":         { bg: "#fde7e9", fg: "#a4262c" },
    "Don't Know":   { bg: "#fff4ce", fg: "#5d4a00" },
  };
  const p = palette[decision] ?? { bg: "#f3f3f3", fg: "#605e5c" };
  return (
    <span style={{
      background: p.bg, color: p.fg,
      fontSize: 12, fontWeight: 600,
      padding: "2px 8px", borderRadius: 10,
      whiteSpace: "nowrap",
    }}>{decision}</span>
  );
}

function flagsCell(item: ReviewItem): React.ReactNode {
  return (
    <>
      {item.accountDisabled && <span title="Entra account disabled" style={{ marginRight: 4 }}>🔒</span>}
      {item.onLongTermLeave && <span title="On long-term leave"      style={{ marginRight: 4 }}>🏖️</span>}
      {item.isSelfReview    && <span title="Self-review — auto-escalated">👤</span>}
    </>
  );
}

function errorMessageFromAxios(err: any, fallback: string): string {
  const status = err?.response?.status;
  const body = err?.response?.data;
  const serverMsg = typeof body === "object" && body !== null ? body.error : null;
  if (status === 401 || status === 403) {
    return "You don't have admin access. Members of the AccessReviewAdmins Entra group can view active cycles.";
  }
  if (serverMsg) return serverMsg;
  return fallback;
}

const AdminCycleDetailPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const { cycleId } = useParams<{ cycleId: string }>();
  const cycleIdNum = cycleId ? parseInt(cycleId, 10) : NaN;

  const [detail,  setDetail]  = React.useState<CycleDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState<string | null>(null);

  const [s1Input,    setS1Input]    = React.useState("");
  const [s2Input,    setS2Input]    = React.useState("");
  const [saving,     setSaving]     = React.useState(false);
  const [saveError,  setSaveError]  = React.useState<string | null>(null);
  const [saveOk,     setSaveOk]     = React.useState(false);

  // ── Phase 4.7 Chunk G — reassign-S2 modal state ─────────────────────────
  const [showReassign,      setShowReassign]      = React.useState(false);
  const [reassignUpn,       setReassignUpn]       = React.useState("");
  const [reassignReason,    setReassignReason]    = React.useState("");
  const [reassignSaving,    setReassignSaving]    = React.useState(false);
  const [reassignError,     setReassignError]     = React.useState<string | null>(null);
  const [reassignResult,    setReassignResult]    = React.useState<ReassignS2Response | null>(null);

  // Live preview of the entered UPN. Same hook + same UX as AdminToolsPage's
  // override-UPN field — admins get instant feedback before they confirm.
  const reassignLookup = useDebouncedUserLookup(reassignUpn, 300);

  React.useEffect(() => {
    if (!account || Number.isNaN(cycleIdNum)) return;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        await getToken(instance, account);
        const fetched = await getCycleDrilldown(cycleIdNum);
        setDetail(fetched);
        setS1Input(isoToDatetimeLocal(fetched.cycle.stage1Deadline));
        setS2Input(isoToDatetimeLocal(fetched.cycle.stage2Deadline));
      } catch (err) {
        setError(errorMessageFromAxios(err, "Failed to load cycle details. Please refresh."));
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [account, instance, cycleIdNum]);

  async function handleSaveDeadlines() {
    if (!detail) return;
    setSaveError(null);
    setSaveOk(false);

    const payload: PatchCyclePayload = {};

    // Only include a field if it differs from the current value
    const currentS1Local = isoToDatetimeLocal(detail.cycle.stage1Deadline);
    const currentS2Local = isoToDatetimeLocal(detail.cycle.stage2Deadline);
    if (s1Input && s1Input !== currentS1Local) payload.stage1Deadline = new Date(s1Input).toISOString();
    if (s2Input && s2Input !== currentS2Local) payload.stage2Deadline = new Date(s2Input).toISOString();

    if (Object.keys(payload).length === 0) {
      setSaveError("Nothing changed. Edit one of the deadlines to save.");
      return;
    }

    const ok = window.confirm(
      `Extend the cycle deadlines?\n\n` +
      (payload.stage1Deadline ? `  Stage 1: ${formatDate(detail.cycle.stage1Deadline)} → ${formatDate(payload.stage1Deadline)}\n` : "") +
      (payload.stage2Deadline ? `  Stage 2: ${formatDate(detail.cycle.stage2Deadline)} → ${formatDate(payload.stage2Deadline)}\n` : "") +
      `\n` +
      `The running orchestration will be notified to re-arm its freeze timer at the new deadline. ` +
      `An audit-log row will be written under your name.\n\n` +
      `Continue?`
    );
    if (!ok) return;

    setSaving(true);
    try {
      await patchCycle(cycleIdNum, payload);
      // Re-fetch the drill-down so the UI reflects the new deadlines plus any
      // backend-side derived state (status transitions, etc).
      const refreshed = await getCycleDrilldown(cycleIdNum);
      setDetail(refreshed);
      setS1Input(isoToDatetimeLocal(refreshed.cycle.stage1Deadline));
      setS2Input(isoToDatetimeLocal(refreshed.cycle.stage2Deadline));
      setSaveOk(true);
    } catch (err) {
      setSaveError(errorMessageFromAxios(err, "Failed to save. Please try again."));
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  // ── Phase 4.7 Chunk G — reassign-S2 actions ─────────────────────────────
  function openReassignModal() {
    setReassignUpn("");
    setReassignReason("");
    setReassignError(null);
    setReassignResult(null);
    setShowReassign(true);
  }

  function closeReassignModal() {
    if (reassignSaving) return; // don't allow closing mid-request
    setShowReassign(false);
    setReassignError(null);
    setReassignResult(null);
  }

  async function handleReassignSubmit() {
    if (!detail) return;
    setReassignError(null);

    const upn = reassignUpn.trim();
    const reason = reassignReason.trim();

    if (!upn) {
      setReassignError("New reviewer UPN is required.");
      return;
    }
    // Client-side gate based on the live preview. The backend re-validates
    // every condition, but failing early gives clearer feedback.
    if (reassignLookup.status === "malformed") {
      setReassignError("New reviewer UPN is malformed; expected an email-shaped value.");
      return;
    }
    if (reassignLookup.status === "not_found") {
      setReassignError("New reviewer UPN does not exist in Entra.");
      return;
    }
    if (reassignLookup.status === "loading") {
      setReassignError("Still validating the UPN in Entra — wait a moment and click Confirm again.");
      return;
    }
    const currentS2 = detail.cycle.s2ReviewerUpn ?? "";
    if (currentS2 && upn.toLowerCase() === currentS2.toLowerCase()) {
      setReassignError("That's already the current Stage 2 reviewer. Pick a different UPN.");
      return;
    }

    // Final confirmation. The Slack DMs and reminderLoop restart are
    // observable side-effects that happen as soon as we commit — make the
    // admin acknowledge that.
    const lookupName = reassignLookup.status === "found"
      ? reassignLookup.result?.displayName ?? upn
      : upn;
    const ok = window.confirm(
      `Reassign the Stage 2 reviewer?\n\n` +
      `  From: ${currentS2 || "(none)"}\n` +
      `  To:   ${lookupName} (${upn})\n` +
      (reason ? `  Reason: ${reason}\n` : "") +
      `\n` +
      `This will:\n` +
      `  • Update review_cycles.s2_reviewer_upn in SQL (atomic with the audit row)\n` +
      `  • Cancel the daily reminders going to the current reviewer\n` +
      `  • Start fresh daily reminders targeting the new reviewer\n` +
      `  • DM the current reviewer that they've been removed\n` +
      `  • DM the new reviewer with the begin-review link\n` +
      `  • Write an audit-log row under your name\n\n` +
      `Continue?`
    );
    if (!ok) return;

    setReassignSaving(true);
    try {
      const resp = await reassignS2(cycleIdNum, {
        newReviewerUpn: upn,
        reason: reason.length > 0 ? reason : undefined,
      });
      setReassignResult(resp);
      // Refresh the drill-down so the cycle metadata (new s2_reviewer_upn) appears.
      const refreshed = await getCycleDrilldown(cycleIdNum);
      setDetail(refreshed);
    } catch (err) {
      setReassignError(errorMessageFromAxios(err, "Reassignment failed. Please try again."));
      console.error(err);
    } finally {
      setReassignSaving(false);
    }
  }

  // ── Guards ────────────────────────────────────────────────────────────────
  if (Number.isNaN(cycleIdNum)) {
    return (
      <div style={pageStyle}>
        <p><Link to="/manage/cycles" style={backLinkStyle}>← Back to cycles</Link></p>
        <div style={errorBoxStyle}>Invalid cycle id in URL.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={pageStyle}>
        <p><Link to="/manage/cycles" style={backLinkStyle}>← Back to cycles</Link></p>
        <p>Loading cycle details…</p>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div style={pageStyle}>
        <p><Link to="/manage/cycles" style={backLinkStyle}>← Back to cycles</Link></p>
        <div style={errorBoxStyle}>{error ?? "Cycle not found."}</div>
      </div>
    );
  }

  const { cycle, items } = detail;
  const inStage2 = cycle.status === "Stage2";
  const deadline = inStage2 ? cycle.stage2Deadline : cycle.stage1Deadline;
  const badge = deadlineBadge(deadline);
  const showS2DeadlineEditor = cycle.stage2Deadline !== null;

  // SOX cadence ceiling — the last day of the trigger month in Berlin. Used
  // ONLY to show a red advisory warning when the admin's selected value
  // exceeds it; the backend no longer rejects past-ceiling deadlines (Internal
  // Audit is consulted out-of-band, and the deadline_change_log captures the
  // change for evidence either way).
  const soxCeiling = cycle.stage1StartedAt ? getMaxAllowedDeadline(new Date(cycle.stage1StartedAt)) : null;

  // Detect whether the CURRENTLY-TYPED value in each picker is past the
  // ceiling. Recomputed on every render so the warning appears immediately
  // as the admin picks a later date and disappears when they back off.
  const s1ParsedInput = s1Input ? new Date(s1Input) : null;
  const s2ParsedInput = s2Input ? new Date(s2Input) : null;
  const s1BeyondMonth = !!(soxCeiling && s1ParsedInput && !Number.isNaN(s1ParsedInput.getTime()) && s1ParsedInput.getTime() > soxCeiling.getTime());
  const s2BeyondMonth = !!(soxCeiling && s2ParsedInput && !Number.isNaN(s2ParsedInput.getTime()) && s2ParsedInput.getTime() > soxCeiling.getTime());

  return (
    <div style={pageStyle}>
      <p style={{ marginBottom: 16 }}>
        <Link to="/manage/cycles" style={backLinkStyle}>← Back to cycles</Link>
      </p>

      {/* Hero */}
      <div style={heroStyle}>
        <div>
          <h2 style={{ margin: 0 }}>{cycle.toolName} — {cycle.cycleName}</h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.92 }}>
            Control ID: <code style={{ background: "rgba(255,255,255,0.18)", padding: "1px 6px", borderRadius: 3 }}>{cycle.controlId}</code>
            {" · "}
            <span style={{ ...statusStyle(cycle.status), background: "rgba(255,255,255,0.92)" }}>{cycle.status}</span>
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginLeft: 32 }}>
          <img src="/trivago-logo.png" alt="trivago" style={{ height: 36 }} />
          <span style={{ fontSize: 11, letterSpacing: "0.08em", opacity: 0.85, marginTop: 4 }}>IT Operations</span>
        </div>
      </div>

      {/* Metadata grid — dynamic columns:
          - When Stage 2 is active (6 cards): 2fr 1fr 1fr 2fr 1fr 1fr,
            so reviewer-UPN cards are 2× the date cards' width.
          - When only Stage 1 (3 cards): 2fr 1fr 1fr, same proportion.
          minmax(0, Xfr) lets long UPNs stay constrained to their column
          instead of forcing the whole grid to widen and wrap. */}
      <div style={{
        ...metadataGridStyle,
        gridTemplateColumns: cycle.stage2StartedAt
          ? "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)"
          : "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)",
      }}>
        <Metadata label="Stage 1 reviewer" value={cycle.s1ReviewerUpn ?? "—"} />
        <Metadata label="Stage 1 started"  value={formatDate(cycle.stage1StartedAt)} />
        <Metadata label="Stage 1 deadline" value={formatDate(cycle.stage1Deadline)} badge={!inStage2 ? badge : null} />
        {cycle.stage2StartedAt && (
          <>
            <Metadata label="Stage 2 reviewer" value={cycle.s2ReviewerUpn ?? "—"} />
            <Metadata label="Stage 2 started"  value={formatDate(cycle.stage2StartedAt)} />
            <Metadata label="Stage 2 deadline" value={formatDate(cycle.stage2Deadline)} badge={inStage2 ? badge : null} />
          </>
        )}
      </div>

      {/* Progress */}
      <h3 style={sectionHeadingStyle}>Progress</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
        <ProgressTile label="Total items"   value={cycle.total} />
        <ProgressTile label="S1 reviewed"   value={cycle.reviewed}   total={cycle.total} />
        <ProgressTile label="Escalated"     value={cycle.escalated} />
        <ProgressTile label="S2 reviewed"   value={cycle.s2Reviewed} total={cycle.escalated} />
      </div>

      {/* Phase 4.7 — Reassign Stage 2 reviewer.
          Decision 10 = B: always-visible section, button disabled outside Stage 2
          with a tooltip. Discoverability beats minimalism — admins learn the
          capability exists even when it's not currently actionable. The backend
          re-validates the Stage 2 constraint on submit. */}
      <h3 style={sectionHeadingStyle}>Reassign Stage 2 reviewer</h3>
      <div style={reassignSectionStyle}>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#323130" }}>
            {inStage2
              ? <>Currently assigned to <strong>{cycle.s2ReviewerUpn ?? "(none)"}</strong>. Use this when the assigned reviewer becomes unavailable mid-cycle (emergency leave, role change, etc.) and the review cannot wait for them to return.</>
              : <>Reassignment is only available while a cycle is in Stage 2. This cycle is currently in <strong>{cycle.status}</strong>; the action is shown here for discoverability but cannot be triggered until Stage 2 starts.</>
            }
          </p>
          {inStage2 && (
            <p style={{ ...hintStyle, marginTop: 6 }}>
              On submit: the SQL update and audit-log row are atomic; the reminder loop targeting the old reviewer is cancelled and a fresh one targeting the new reviewer is started; both reviewers are DM'd.
            </p>
          )}
        </div>
        <button
          onClick={openReassignModal}
          disabled={!inStage2}
          title={inStage2 ? undefined : "Reassignment is only available while the cycle is in Stage 2."}
          style={{
            ...primaryBtnStyle,
            whiteSpace: "nowrap",
            opacity: inStage2 ? 1 : 0.5,
            cursor: inStage2 ? "pointer" : "not-allowed",
          }}
        >
          Reassign Stage 2 reviewer…
        </button>
      </div>

      {/* Deadline editor */}
      <h3 style={sectionHeadingStyle}>Extend deadlines</h3>
      <div style={infoBoxStyle}>
        <span style={{ fontSize: 16 }}>ℹ️</span>
        <span>
          Extensions can only move deadlines later, never earlier. Each successful change re-arms the
          running orchestration's freeze timer and writes an audit-log row under your name.
          {soxCeiling && <>
            {" "}The current SOX cadence target for this cycle is <strong>{formatDate(soxCeiling.toISOString())}</strong> (last day
            of the trigger month). Extending past that point requires Internal Audit consultation — see the warning below if applicable.
          </>}
        </span>
      </div>

      <div style={deadlineEditorStyle}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div>
            <label style={labelStyle}>Stage 1 deadline</label>
            <input
              type="datetime-local"
              value={s1Input}
              onChange={(e) => setS1Input(e.target.value)}
              disabled={saving || cycle.status !== "Stage1"}
              style={inputStyle}
            />
            {cycle.status !== "Stage1" && (
              <p style={hintStyle}>Stage 1 is no longer active — deadline locked.</p>
            )}
            {s1BeyondMonth && cycle.status === "Stage1" && (
              <div style={redWarningBoxStyle}>
                <strong>⚠ You are extending the Access Review beyond the end of this month!</strong>
                {" "}Please consult with the Internal Audit team first, before making such a change.
                Click <em>Cancel</em> if this has not been discussed yet.
              </div>
            )}
          </div>
          {showS2DeadlineEditor ? (
            <div>
              <label style={labelStyle}>Stage 2 deadline</label>
              <input
                type="datetime-local"
                value={s2Input}
                onChange={(e) => setS2Input(e.target.value)}
                disabled={saving || cycle.status !== "Stage2"}
                style={inputStyle}
              />
              {cycle.status !== "Stage2" && (
                <p style={hintStyle}>Stage 2 is no longer active — deadline locked.</p>
              )}
              {s2BeyondMonth && cycle.status === "Stage2" && (
                <div style={redWarningBoxStyle}>
                  <strong>⚠ You are extending the Access Review beyond the end of this month!</strong>
                  {" "}Please consult with the Internal Audit team first, before making such a change.
                  Click <em>Cancel</em> if this has not been discussed yet.
                </div>
              )}
            </div>
          ) : (
            <div>
              <label style={labelStyle}>Stage 2 deadline</label>
              <p style={hintStyle}>
                Not set — the cycle has not entered Stage 2 yet.
              </p>
            </div>
          )}
        </div>

        {saveError && <div style={{ ...errorBoxStyle, marginTop: 12 }}>{saveError}</div>}
        {saveOk && <div style={{ ...successBoxStyle, marginTop: 12 }}>Deadlines updated.</div>}

        <div style={{ marginTop: 16 }}>
          <button onClick={handleSaveDeadlines} disabled={saving} style={primaryBtnStyle}>
            {saving ? "Saving…" : "Save deadline changes"}
          </button>
        </div>
      </div>

      {/* Items */}
      <h3 style={sectionHeadingStyle}>
        Review items
        <span style={countBadgeStyle}>{items.length}</span>
      </h3>

      {items.length === 0 ? (
        <p style={{ color: "#605e5c", fontStyle: "italic" }}>No items in this cycle.</p>
      ) : (
        <table style={tableStyle}>
          <colgroup>
            <col style={{ width: "20%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "13%" }} />
          </colgroup>
          <thead>
            <tr style={{ background: "#f3f3f3", textAlign: "left" }}>
              <th style={thStyle}>Member</th>
              <th style={thStyle}>Group</th>
              <th style={thStyle}>Team</th>
              <th style={thStyle}>Flags</th>
              <th style={thStyle} title="Stage 1 decision (hover for justification)">S1 Decision</th>
              <th style={thStyle}>S1 Reviewer</th>
              <th style={thStyle} title="Stage 2 decision (hover for justification)">S2 Decision</th>
              <th style={thStyle}>S2 Reviewer</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600, fontSize: 13.5 }}>{item.memberDisplayName}</div>
                  <div style={{ fontSize: 11.5, color: "#888" }}>{item.memberUpn}</div>
                </td>
                <td style={tdStyle}>{item.groupName}</td>
                <td style={tdStyle}>{item.teamName ?? <span style={{ color: "#888" }}>—</span>}</td>
                <td style={tdStyle}>{flagsCell(item)}</td>
                <td style={tdStyle} title={item.s1Justification ?? undefined}>
                  {decisionBadge(item.s1Decision)}
                </td>
                <td style={tdStyle}>
                  {item.s1ReviewedBy ? (
                    <>
                      <div style={{ fontSize: 13 }}>{item.s1ReviewedBy}</div>
                      {item.s1ReviewedDate && (
                        <div style={{ fontSize: 11, color: "#888" }}>{formatDate(item.s1ReviewedDate)}</div>
                      )}
                    </>
                  ) : <span style={{ color: "#888" }}>—</span>}
                </td>
                <td style={tdStyle} title={item.s2Justification ?? undefined}>
                  {decisionBadge(item.s2Decision)}
                </td>
                <td style={tdStyle}>
                  {item.s2ReviewedBy ? (
                    <>
                      <div style={{ fontSize: 13 }}>{item.s2ReviewedBy}</div>
                      {item.s2ReviewedDate && (
                        <div style={{ fontSize: 11, color: "#888" }}>{formatDate(item.s2ReviewedDate)}</div>
                      )}
                    </>
                  ) : <span style={{ color: "#888" }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Phase 4.7 Chunk G — reassign-S2 modal */}
      {showReassign && (
        <div
          style={modalOverlayStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reassign-modal-title"
          onClick={closeReassignModal}
        >
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h3 id="reassign-modal-title" style={{ margin: "0 0 4px", color: "#0078d4" }}>
              Reassign Stage 2 reviewer
            </h3>
            <p style={{ fontSize: 12.5, color: "#605e5c", margin: "0 0 16px" }}>
              {cycle.toolName} — {cycle.cycleName} · currently <code style={codeInlineStyle}>{cycle.s2ReviewerUpn ?? "(none)"}</code>
            </p>

            {reassignResult ? (
              <ReassignResultPanel result={reassignResult} onClose={() => { setShowReassign(false); setReassignResult(null); }} />
            ) : (
              <>
                <div style={{ marginBottom: 12 }}>
                  <label style={labelStyle}>New reviewer UPN <span style={requiredStyle}>*</span></label>
                  <input
                    type="text"
                    value={reassignUpn}
                    onChange={(e) => setReassignUpn(e.target.value)}
                    disabled={reassignSaving}
                    placeholder="new.reviewer@trivago.com"
                    style={inputStyle}
                    autoFocus
                  />
                  <ReassignLookupPreview upn={reassignUpn} lookup={reassignLookup} />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>
                    Reason <span style={{ color: "#888", fontWeight: 400 }}>(optional)</span>
                  </label>
                  <textarea
                    value={reassignReason}
                    onChange={(e) => setReassignReason(e.target.value)}
                    disabled={reassignSaving}
                    rows={3}
                    placeholder="e.g. Original reviewer is on emergency leave for the rest of the cycle."
                    style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical" }}
                  />
                  <p style={hintStyle}>
                    If provided, the reason is stored in the cycle audit log AND included in the Slack DMs to both the old and new reviewer.
                  </p>
                </div>

                {reassignError && <div style={{ ...errorBoxStyle, marginBottom: 12 }}>{reassignError}</div>}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button type="button" onClick={closeReassignModal} disabled={reassignSaving} style={secondaryBtnStyle}>
                    Cancel
                  </button>
                  <button type="button" onClick={handleReassignSubmit} disabled={reassignSaving} style={primaryBtnStyle}>
                    {reassignSaving ? "Reassigning…" : "Confirm reassignment"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Live-preview subcomponent for the reassign modal ────────────────────────
// Mirrors AdminToolsPage's OverrideLookupPreview but slightly different copy
// — this preview also surfaces the no-Slack-ID case prominently, because the
// reassign endpoint specifically depends on Slack to notify the new reviewer.
const ReassignLookupPreview: React.FC<{
  upn: string;
  lookup: ReturnType<typeof useDebouncedUserLookup>;
}> = ({ upn, lookup }) => {
  if (upn.trim().length === 0) return null;

  switch (lookup.status) {
    case "idle":
      return null;
    case "malformed":
      return (
        <div style={previewNeutralStyle}>
          <span style={{ color: "#888" }}>Type a complete email-shaped UPN to validate against Entra.</span>
        </div>
      );
    case "loading":
      return (
        <div style={previewNeutralStyle}>
          <span style={{ color: "#888" }}>Looking up in Entra…</span>
        </div>
      );
    case "found": {
      const result = lookup.result!;
      const disabled = result.accountEnabled === false;
      const noSlack = !result.slackId;
      // "warn" if account is disabled OR there's no Slack ID. Both cases
      // commit the reassignment server-side but produce degraded outcomes
      // the admin should know about up front.
      const showWarn = disabled || noSlack;
      return (
        <div style={showWarn ? previewWarnStyle : previewOkStyle}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>
            {showWarn ? "⚠ " : "✓ "}
            {result.displayName ?? "(no display name)"}
            {result.jobTitle && (
              <span style={{ fontWeight: 400, color: "#605e5c" }}> — {result.jobTitle}</span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: showWarn ? "#5d4a00" : "#107c10" }}>
            {disabled && <div>Entra account is currently disabled.</div>}
            {noSlack && (
              <div>
                No Slack ID resolved (Jira-UserProps + Slack lookup empty).
                The reassignment will commit, but the new reviewer will not receive a Slack DM —
                a paste-ready alert is posted to the admin channel instead so you can deliver the link manually.
              </div>
            )}
            {!disabled && !noSlack && <>Found in Entra · Slack ID <code style={codeInlineStyle}>{result.slackId}</code></>}
          </div>
        </div>
      );
    }
    case "not_found":
      return (
        <div style={previewErrorStyle}>
          ✗ No user found in Entra with this UPN.
        </div>
      );
    case "error":
      return (
        <div style={previewErrorStyle}>
          Lookup failed: {lookup.errorMessage ?? "unknown error"}
        </div>
      );
  }
};

// ── Result panel shown in-modal after a successful reassignment ─────────────
const ReassignResultPanel: React.FC<{
  result: ReassignS2Response;
  onClose: () => void;
}> = ({ result, onClose }) => {
  return (
    <div>
      <div style={{ ...successBoxStyle, marginBottom: 12 }}>
        Stage 2 reviewer reassigned. <strong>{result.newReviewerDisplayName}</strong> ({result.newReviewerUpn}) is now the active reviewer.
      </div>
      <div style={{ fontSize: 13, color: "#323130", marginBottom: 12 }}>
        <div>From: <code style={codeInlineStyle}>{result.oldReviewerUpn}</code></div>
        <div>To: <code style={codeInlineStyle}>{result.newReviewerUpn}</code></div>
        {result.reminderInstanceId && (
          <div style={{ fontSize: 11.5, color: "#605e5c", marginTop: 4 }}>
            New reminder loop instance: <code style={codeInlineStyle}>{result.reminderInstanceId}</code>
          </div>
        )}
      </div>

      {result.issues && result.issues.length > 0 && (
        <div style={previewWarnStyle}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            ⚠ Reassignment committed, but some best-effort steps had issues:
          </div>
          <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: 12.5 }}>
            {result.issues.map((issue, i) => (
              <li key={i}>{issue}</li>
            ))}
          </ul>
          <div style={{ marginTop: 6, fontSize: 11.5, color: "#5d4a00" }}>
            Check the admin alerts Slack channel for paste-ready details where applicable.
          </div>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button type="button" onClick={onClose} style={primaryBtnStyle}>Close</button>
      </div>
    </div>
  );
};

// ── Subcomponents ───────────────────────────────────────────────────────────
const Metadata: React.FC<{ label: string; value: string; badge?: { text: string; color: string } | null }> = ({ label, value, badge }) => (
  <div style={metadataItemStyle}>
    <div style={{ fontSize: 11, fontWeight: 600, color: "#605e5c", textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {label}
    </div>
    <div style={{ fontSize: 14, marginTop: 4, color: "#323130", overflowWrap: "break-word", wordBreak: "break-word" }}>{value}</div>
    {badge && (
      <span style={{
        display: "inline-block", marginTop: 4,
        fontSize: 11, fontWeight: 600,
        padding: "1px 7px", borderRadius: 8,
        background: badge.color === "#d13438" ? "#fde7e9" : badge.color === "#ff8c00" ? "#fff4ce" : "#f3f3f3",
        color: badge.color,
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}>{badge.text}</span>
    )}
  </div>
);

const ProgressTile: React.FC<{ label: string; value: number; total?: number }> = ({ label, value, total }) => {
  const pct = total !== undefined && total > 0 ? Math.round((value / total) * 100) : null;
  return (
    <div style={progressTileStyle}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#605e5c", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 6, color: "#323130" }}>
        {value}{total !== undefined && <span style={{ fontSize: 14, color: "#888" }}> / {total}</span>}
      </div>
      {pct !== null && (
        <div style={{ marginTop: 8, background: "#e1dfdd", borderRadius: 4, height: 6, overflow: "hidden" }}>
          <div style={{ background: "#0078d4", height: "100%", width: `${pct}%`, transition: "width 0.3s ease" }} />
        </div>
      )}
    </div>
  );
};

// ── Styles ──────────────────────────────────────────────────────────────────
const pageStyle: React.CSSProperties = {
  padding: 24,
  fontFamily: "Segoe UI, sans-serif",
  maxWidth: 1300,
  margin: "0 auto",
};

const backLinkStyle: React.CSSProperties = {
  color: "#0078d4",
  textDecoration: "none",
  fontSize: 13.5,
  fontWeight: 500,
};

const heroStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  padding: "16px 24px",
  marginBottom: 24,
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const metadataGridStyle: React.CSSProperties = {
  display: "grid",
  gap: 14,
  marginBottom: 16,
};

const metadataItemStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid #e1dfdd",
  borderRadius: 4,
  padding: "12px 14px",
  minWidth: 0, // allow long UPNs to wrap within their grid column instead of overflowing
};

const progressTileStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid #e1dfdd",
  borderRadius: 4,
  padding: "14px 16px",
};

const sectionHeadingStyle: React.CSSProperties = {
  borderBottom: "2px solid #0078d4",
  paddingBottom: 8,
  marginTop: 32,
  color: "#0078d4",
  display: "flex",
  alignItems: "center",
  gap: 10,
};

const infoBoxStyle: React.CSSProperties = {
  background: "#deecf9",
  border: "1px solid #71afe5",
  borderRadius: 4,
  padding: "12px 16px",
  marginBottom: 16,
  display: "flex",
  alignItems: "flex-start",
  gap: 10,
  fontSize: 13,
  color: "#004578",
  lineHeight: 1.5,
};

const hintStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: "#888",
  marginTop: 6,
};

const redWarningBoxStyle: React.CSSProperties = {
  marginTop: 10,
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  padding: "10px 12px",
  color: "#a4262c",
  fontSize: 12.5,
  lineHeight: 1.5,
};

const deadlineEditorStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid #e1dfdd",
  borderRadius: 4,
  padding: 20,
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12.5,
  fontWeight: 600,
  color: "#323130",
  marginBottom: 6,
};

const requiredStyle: React.CSSProperties = {
  color: "#d13438",
  marginLeft: 2,
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  fontSize: 13,
  border: "1px solid #ccc",
  borderRadius: 4,
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const errorBoxStyle: React.CSSProperties = {
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  padding: "10px 16px",
  color: "#a4262c",
  fontSize: 13,
};

const successBoxStyle: React.CSSProperties = {
  background: "#dff6dd",
  border: "1px solid #107c10",
  borderRadius: 4,
  padding: "10px 16px",
  color: "#107c10",
  fontSize: 13,
};

const countBadgeStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  borderRadius: 12,
  padding: "1px 10px",
  fontSize: 13,
  fontWeight: 600,
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
  tableLayout: "fixed",
};

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 600,
  borderBottom: "1px solid #ddd",
};

const tdStyle: React.CSSProperties = {
  padding: "10px 12px",
  verticalAlign: "middle",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "#0078d4",
  color: "white",
  border: "none",
  padding: "8px 18px",
  fontSize: 13,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

const secondaryBtnStyle: React.CSSProperties = {
  background: "white",
  color: "#0078d4",
  border: "1px solid #0078d4",
  padding: "7px 17px",
  fontSize: 13,
  borderRadius: 4,
  cursor: "pointer",
  fontFamily: "inherit",
};

const reassignSectionStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid #e1dfdd",
  borderRadius: 4,
  padding: 20,
  display: "flex",
  alignItems: "center",
  gap: 16,
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: 24,
};

const modalStyle: React.CSSProperties = {
  background: "white",
  borderRadius: 6,
  padding: 24,
  width: "100%",
  maxWidth: 540,
  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
  maxHeight: "90vh",
  overflowY: "auto",
};

// Live-preview status boxes (shared visual language with AdminToolsPage).
const previewNeutralStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  background: "#f3f2f1",
  border: "1px solid #e1dfdd",
  borderRadius: 4,
  fontSize: 12.5,
};

const previewOkStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  background: "#dff6dd",
  border: "1px solid #92c353",
  borderRadius: 4,
  fontSize: 12.5,
  color: "#107c10",
};

const previewWarnStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  background: "#fff4ce",
  border: "1px solid #f9d77e",
  borderRadius: 4,
  fontSize: 12.5,
  color: "#5d4a00",
};

const previewErrorStyle: React.CSSProperties = {
  marginTop: 8,
  padding: "8px 12px",
  background: "#fde7e9",
  border: "1px solid #d13438",
  borderRadius: 4,
  fontSize: 12.5,
  color: "#a4262c",
};

const codeInlineStyle: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: 11.5,
  background: "rgba(0,0,0,0.06)",
  padding: "1px 4px",
  borderRadius: 3,
};

export default AdminCycleDetailPage;
